// packages/lib/constants/FeatureFlags.ts
export enum FeatureFlagKeys {
  LoginButtonTest = 'login-button-test',
  SupportAssistantEnabled = 'support-assistant-enabled',
  DemoNewDashboardLayout = 'demo-new-dashboard-layout',
  MobileSetupNavigation = 'mobile-setup-navigation',
  ClientAlertsEnabled = 'client-alerts-enabled',
  BigQueryReporting = 'bigquery-reporting',
  PiiRedactionEnabled = 'pii-redaction-enabled',
  BookingWindowOverrides = 'booking-window-overrides',
  ReportXlsxExport = 'report-xlsx-export',
  AssistantSidenavEnabled = 'assistant-sidenav-enabled',
  // OC-879 — the Chats pill in the assistant sidebar. Hidden in OC-791; this
  // flag lets it come back without another code change either way.
  AssistantChatsTabEnabled = 'assistant-chats-tab-enabled',
  // Also read by the member app (mobile useNewsFeedAvailable) for the News Feed /
  // Content Suite family — banners, walkthroughs, articles and the awards gate.
  ContentSuiteEnabled = 'content-suite-enabled',
  // Read only by the member app (mobile useMyListEnabled). Wider than the name:
  // it gates the My List entry on both Content Suite headers AND the
  // Mark-as-Complete / Add-to-List row on every article layout, so turning it off
  // to hide My List also removes article completion. MEM-867 replaced a hardcoded
  // constant that shipped all five always-on, so create this flag and enable it
  // BEFORE the build ships or members lose features that work today.
  ContentSuiteMyListEnabled = 'content-suite-my-list-enabled',
  AnalyticsTabEnabled = 'analytics-tab-enabled',
  // INT-753 — one flag for the whole Daily Snapshot page redesign: "Daily snapshot"
  // page title, expandable Analytics nav, and the corporate currency banner.
  DailySnapshotPageEnabled = 'daily-snapshot-page-enabled',
  DailySnapshotV2PageEnabled = 'daily-snapshot-v2-page-enabled',
  // INT-1169 — Classic dashboards entry in the Reports left nav. Default OFF;
  // shown only where PostHog release conditions target it, on top of the
  // per-corporate BiMigrationConfig opt-in the classics gate already enforces.
  ClassicDashboardsNavEnabled = 'classic-dashboards-nav-enabled',
  CubeGrossRevenueCard = 'cube-gross-revenue-card',
  CubeBookingUtilisationCard = 'cube-booking-utilisation-card',
  CubeEquipmentUtilisationCard = 'cube-equipment-utilisation-card',
  CubeActiveMembersCard = 'cube-active-members-card',
  // INT-1887 — Brand twin of CubeActiveMembersCard. Replaces the Total Active
  // Members tile on the Brand overview with a currency-scoped card (headline
  // + composition + Cancelling + Suspended). Reads BrandHomepageActiveMembers.
  CubeBrandHomepageActiveMembersCard = 'cube-brand-homepage-active-members-card',
  CubeNewMembersCard = 'cube-new-members-card',
  CubeServicesSoldCard = 'cube-services-sold-card',
  CubeHoldingBothMembersCard = 'cube-holding-both-members-card',
  CubeTop5ServicesCard = 'cube-top-5-services-card',
  CubeMemberCompositionCard = 'cube-member-composition-card',
  CubeMemberMomentumCard = 'cube-member-momentum-card',
  CubeSuspendedMembersCard = 'cube-suspended-members-card',
  CubeDashboardFiltersPanel = 'cube-dashboard-filters-panel',
  ClassPerformanceFilters = 'class-performance-filters',
  CubeMemberLossSplitCard = 'cube-member-loss-split-card',
  CubeMemberGrowthSplitCard = 'cube-member-growth-split-card',
  // OC-664 — staff-facing backup-card designation and failover visibility. The charge path
  // itself is gated by the Hapana-admin site toggle, not by this flag.
  BankFailoverToCardEnabled = 'bank-failover-to-card-enabled',
  // OC-1980 — the card failover retry set: walk every card on the day, keep dunning the soft
  // declines. Gates the charge path itself, on sites whose failover toggle is already on.
  CardFailoverRetrySetEnabled = 'card-failover-retry-set-enabled',
  CubeMemberMovementTrendChart = 'cube-member-movement-trend-chart',
  CubeRecurringMovementKpiCard = 'cube-recurring-movement-kpi-card',
  CubeSevenDayMomentumCard = 'cube-seven-day-momentum-card',
  CubeMonthlyPacingMtdCard = 'cube-monthly-pacing-mtd-card',
  CubeQuarterlyPacingCard = 'cube-quarterly-pacing-card',
  CubeGrossRevenueTrendCard = 'cube-gross-revenue-trend-card',
  CubeSessionsDeliveredCard = 'cube-sessions-delivered-card',
  // INT-239 — gates the whole Class Performance analytics tab (sidebar item +
  // route visibility). OFF ⇒ tab hidden from the analytics tabs bar AND the
  // dashboard body renders nothing even on a deep-linked URL. Individual
  // widgets inside the tab (Sessions Delivered, etc.) have their own per-card
  // flags for gradual rollout within an enabled dashboard.
  ClassPerformancePageEnabled = 'class-performance-page-enabled',
  // INT-247 — gates the whole Revenue analysis analytics tab (tab visibility +
  // body). OFF ⇒ tab hidden from the tabs bar AND the dashboard body renders
  // nothing even on a deep-linked URL. Cards inside carry their own flags.
  RevenueAnalysisPageEnabled = 'revenue-analysis-page-enabled',
  // INT-240 — gates the Revenue Trend & Mix sub-page of Revenue analysis: the
  // sub-page tab strip and the area beneath it, at both scopes. Widgets inside
  // carry their own flags, mirroring how RevenueAnalysisPageEnabled gates the
  // tab while each card gates itself. OFF ⇒ no strip and no sub-page. ON with
  // every child flag off leaves an empty sub-page — a transient rollout state,
  // not a user-facing one.
  RevenueTrendMixPageEnabled = 'revenue-trend-mix-page-enabled',
  // INT-247 — Gross Revenue KPI card on the Revenue analysis page.
  CubeRevenueAnalysisGrossRevenueCard = 'cube-revenue-analysis-gross-revenue-card',
  // INT-246 — Avg Revenue per Location KPI card on the Revenue analysis page.
  // Brand-scope only; the card returns null at location scope where the avg
  // collapses to gross and count = 1.
  CubeRevenueAnalysisAvgRevenuePerLocationCard = 'cube-revenue-analysis-avg-revenue-per-location-card',
  // INT-1404 — the expandable detail panel beneath the Gross Revenue KPI card,
  // and the card's expand toggle that opens it. Separate from the card flag so
  // the breakdown can be dark-launched (and killed) wherever the card is
  // already live; OFF ⇒ the card renders with no expand affordance at all.
  CubeRevenueAnalysisGrossRevenuePanel = 'cube-revenue-analysis-gross-revenue-panel',
  // INT-1459 — the expandable detail panel beneath the Avg Revenue per Location
  // KPI card (top / lowest / spread + per-location deviation table), and the
  // card's expand toggle that opens it. Brand-scope only — panel returns null
  // at location scope. Separate from the card flag on purpose (mirrors INT-1404).
  CubeRevenueAnalysisAvgRevenuePerLocationDetails = 'cube-revenue-analysis-avg-revenue-per-location-details',
  // INT-244 — Revenue Growth % (YoY) KPI card on the Revenue analysis page.
  // Phase 1 is the hero only; the expandable growth-quality panel is INT-1523.
  CubeRevenueAnalysisRevenueGrowthYoyCard = 'cube-revenue-analysis-revenue-growth-yoy-card',
  CubeRevenueAnalysisRevenueGrowthYoyPanel = 'cube-revenue-analysis-revenue-growth-yoy-panel',
  // INT-245 — Paying Members KPI card on the Revenue analysis page. A headcount
  // snapshot on the last day of the selected period, so the currency lens does
  // not apply to it. Phase 1 is the hero and delta chip only; the composition
  // panel and its expand toggle are INT-1560.
  CubeRevenueAnalysisTotalActiveMembersCard = 'cube-revenue-analysis-total-active-members-card',
  // INT-1560 — the expandable composition panel beneath the Paying Members KPI
  // card (membership / package / holding-both split, each with a gender donut),
  // and the card's expand toggle that opens it. Separate from the card flag so
  // the panel can be dark-launched (and killed) wherever the card is already
  // live; OFF ⇒ the card renders with no expand affordance at all.
  CubeRevenueAnalysisTotalActiveMembersPanel = 'cube-revenue-analysis-total-active-members-panel',
  // INT-1591 — ARPM (Avg Recurring Revenue per Member) KPI card on the Revenue
  // analysis page. Location-scope only: it fills the Revenue Summary tile that
  // Avg Revenue per Location vacates at location scope. Hero + delta + member
  // sub-row; no panel in this phase.
  CubeRevenueAnalysisArpmCard = 'cube-revenue-analysis-arpm-card',
  // INT-1592 — the expandable ARPM by service panel beneath the ARPM KPI card
  // (top / lowest / spread + per-service deviation table), and the card's expand
  // toggle that opens it. Location-scope only, inheriting the card's scope.
  // Separate from the card flag so the panel can be dark-launched (and killed)
  // wherever the card is already live; OFF ⇒ the card renders with no expand
  // affordance at all.
  CubeRevenueAnalysisArpmPanel = 'cube-revenue-analysis-arpm-panel',
  // INT-240 — Revenue composition & momentum card, the first widget on the
  // Revenue Trend & Mix sub-page. Gates the card only; the sub-page and its tab
  // strip are gated separately by RevenueTrendMixPageEnabled. OFF ⇒ the card
  // does not render and no Cube query fires. Renders at brand and location
  // scope. Weekly view only — the Monthly and Quarterly buttons render disabled
  // until their own tickets land.
  CubeRevenueAnalysisCompositionMomentumCard = 'cube-revenue-analysis-composition-momentum-card',
  // INT-1794 — ARPM over time card, the second widget on the Revenue Trend & Mix
  // sub-page, directly beneath Revenue composition & momentum. Gates the card
  // only; the sub-page and its tab strip are gated separately by
  // RevenueTrendMixPageEnabled. OFF ⇒ the card does not render and none of its
  // thirty Cube queries fire. Location scope only — a brand average moves with
  // the mix of locations rather than with yield, so there is no brand variant.
  CubeRevenueAnalysisArpmOverTimeCard = 'cube-revenue-analysis-arpm-over-time-card',
  // INT-1815 — Avg revenue per location over time card, the third widget on the
  // Revenue Trend & Mix sub-page. Gates the card only; the sub-page and its tab
  // strip are gated separately by RevenueTrendMixPageEnabled. OFF ⇒ the card
  // does not render and no Cube query fires. Corporate scope only — an average
  // across locations is undefined for a single location, so there is no location
  // variant. Never on screen beside ARPM over time, which is location only, so
  // at either scope exactly one of the two renders.
  CubeRevenueAnalysisAvgRevenuePerLocationOverTimeCard = 'cube-revenue-analysis-avg-revenue-per-location-over-time-card',
  // INT-230 — Attendance Utilisation % KPI (threshold colouring + PoP delta) on Class Performance.
  CubeAttendanceUtilisationCard = 'cube-attendance-utilisation-card',
  // INT-232 — Booking Utilisation % KPI on Class Performance (distinct from homepage CubeBookingUtilisationCard).
  CubeClassPerformanceBookingUtilisationCard = 'cube-class-performance-booking-utilisation-card',
  // INT-231 — Booking-to-Attendance Gap KPI (threshold + inverse delta + widening alert).
  CubeBookingAttendanceGapCard = 'cube-booking-attendance-gap-card',
  // INT-979 — Capacity Funnel widget on Class Performance. Ships with the Capacity
  // row (total spots offered across delivered CLASS sessions); other rows
  // (Booked, Attended, No-show, Late cancel) land in follow-up PRs.
  CubeCapacityFunnelCard = 'cube-capacity-funnel-card',
  // INT-1038 — Booked vs Attended over time trend chart on Class Performance.
  // First widget of the Trends section (INT-1037), sits directly beneath the
  // Total Leakage banner. Dual-line chart with a shaded amber leakage band
  // and a 30-day / 12-month toggle.
  CubeClassPerformanceBookedAttendedTrendCard = 'cube-class-performance-booked-attended-trend-card',
  // INT-1051 — Booking outcomes by day (Show-up / No-show / Late cancel) widget
  // on Class Performance, Attendance & Occupancy section, location-level.
  // REPLACES the legacy Attendance utilisation KPI in that section. Two-panel
  // widget: session list (left) + day-of-week bars (right). 3-way metric
  // toggle swaps title/subtitle/colours client-side; single Cube query at
  // session × day-of-week grain.
  CubeClassPerformanceBookingOutcomesByDayCard = 'cube-class-performance-booking-outcomes-by-day-card',
  CubeClassPerformanceBookingOutcomesByLocationCard = 'cube-class-performance-booking-outcomes-by-location-card',
  // INT-226 — Attendance Utilisation Heatmap on Class Performance, Attendance
  // & Occupancy section. 7 × N (day × hour block) grid. attended ÷ capacity
  // per cell, CLASS sessions only. Ships behind this flag from day 1 — see
  // Lesson 115 (flag-gate-day-one).
  CubeClassPerformanceAttendanceUtilisationHeatmapCard = 'cube-class-performance-attendance-utilisation-heatmap-card',
  // INT-1197 — Class leaderboard (Top • most full + Bottom • least full) widget
  // on Class Performance, Class performance section, location-level. Two-panel
  // widget: green ranked list (left) + red ranked list (right). Single Cube
  // (ClassPerformanceClassLeaderboard); hook fires it twice (current + previous window) and
  // computes delta_pp client-side per class. Both panels share one fetch.
  CubeClassLeaderboardCard = 'cube-class-leaderboard-card',
  // INT-1199 — Services by Visits leaderboard on Class Performance dashboard,
  // Highest / Lowest toggle. Single Cube (ClassPerformanceServicesByVisitsLeaderboard);
  // hook fires it twice (current + previous window) and computes visits % change
  // client-side per service. Brand-capable via buildSiteScopeFilter — renders at
  // both site (/s/<siteId>/reports) and corporate (/c/reports) levels day one.
  CubeServicesByVisitsLeaderboardCard = 'cube-services-by-visits-leaderboard-card',
  ImportUtility = 'import-utility',
  PublicBundledSigning = 'public-bundled-signing',
  SelfServiceEnabled = 'self-service-enabled',
  MemberExperienceSurfaceOverridesEnabled = 'member-experience-surface-overrides-enabled',
  DelayFirstPaymentEnabled = 'delay-first-payment-enabled',
  SandboxManagementEnabled = 'sandbox-management',
  DoorAccessEnabled = 'door-access-enabled',
  // INT-314 — gates only the offline member-DB push to door panels, per site. Verdict
  // computation and reconcile-health run regardless. Separate from DoorAccessEnabled so the
  // first push (which flips a panel's offline behaviour from fail-closed to list-open) has
  // its own kill-switch and can be piloted before the fleet.
  DoorOfflineMemberSync = 'door-offline-member-sync',
  // INT-952 — enforces multi-door access rules per site: a door in a non-main zone becomes a
  // secondary door (session-type coverage + zone schedule, DoorAccessEvent log, no check-in,
  // no credit) and an unresolvable or unzoned door denies as UNKNOWN_ACCESS_POINT. OFF /
  // unresolved / error all fail closed to TODAY'S behaviour — every door treated as main —
  // never to "deny", so a flag failure can never lock a club out of its own front door.
  MultiDoorAccessRules = 'multi-door-access-rules',
  MultiDoorCheckInZones = 'multi-door-check-in-zones',
  // Multi-Doors Phase 3 — lets staff author GENDER and AGE clauses on a zone. Gates AUTHORING
  // only: a clause already stored is evaluated at every door regardless of the flag, because
  // "flag off, ignore the 18+ rule" would fail open. Rollback is removing the clause.
  MultiDoorIdentityClauses = 'multi-door-identity-clauses',
  SiteWebhookLifecycleEvents = 'site-webhook-lifecycle-events',
  // INT-747 — offers DRAGONFLY as a selectable provider when registering a door. Separate from
  // DoorAccessEnabled (the whole-surface switch) so a pull-based partner can be piloted at one
  // site without hiding the VIVA doors every other site already runs on. With it off the drawer
  // keeps its locked single-provider field exactly as before.
  DragonflyDoorProvider = 'dragonfly-door-provider',
  InceptionDoorProvider = 'inception-door-provider',
  InnerIntelligenceDoorProvider = 'inner-intelligence-door-provider',
  // INT-1858 — rollout switch for GET /business/access/doors. Fail-closed: the route answers 404
  // FEATURE_NOT_ENABLED until the flag is on for the corporate (or site), so door discovery can be
  // staged per brand and turned off without a deploy while the feed and tap stay up.
  PartnerAccessDoors = 'partner-access-doors',
  MarketplaceHubspotEnabled = 'marketplace-hubspot-enabled',
  // OC-1492 — gates creating Prospect clients from marketplace contacts, and the
  // Prospect surfaces in Core. Separate from MarketplaceHubspotEnabled so consent
  // sync can stay on for a brand while prospect creation is off: this one writes
  // new client rows, so it is the kill-switch if the volume lands badly.
  MarketplaceProspectClientsEnabled = 'marketplace-prospect-clients-enabled',
  BarcodeCheckInEnabled = 'barcode-check-in-enabled',
  BrandedEmailTemplateEnabled = 'branded-email-template-enabled',
  MemberMovementEnabled = 'member-movement-enabled',
  /**
   * OC-1461 — the rolling APPROVAL posture's Incoming tab. Separate from
   * MemberMovementEnabled (which gates the whole module) so the intake queue can be rolled out
   * per-brand, and switched off, without taking period runs down with it.
   */
  MemberMovementIncomingApproval = 'member-movement-incoming-approval',
  MemberMovementLocationPosture = 'member-movement-location-posture',
  // OC-8 / OC-13 — gates the whole Shared Credits feature (config + sharing surfaces).
  // NOTE: confirm this key matches the existing PostHog flag (id 201626).
  SharedCreditsEnabled = 'shared-credits-enabled',
  // OC-119 — gates the Guest Pass V2 runtime (send/claim entry points, cron grant,
  // sites GUEST_PASS branches). Per-brand rollout: distinctId = corporateId.
  GuestPassesEnabled = 'guest-passes-enabled',
  // Gates the mobile "Browse as a guest" entry point and the whole
  // guest-browsing mode. Unrelated to GuestPassesEnabled above.
  GuestBrowsingEnabled = 'guest-browsing-enabled',
  // Gates guest gift-card purchase, on top of GuestBrowsingEnabled.
  GuestGiftCardEnabled = 'guest-gift-card-enabled',
  // OC-594 — gates a brand's own Explore booking tabs (City Fitness: Studio /
  // CF Thrive / Revive). Evaluated only in the mobile app, which identifies as
  // the member (distinctId = clientID) and registers the brand as a PostHog
  // *group*: `corporate` = the brand config's v2CorporateID, falling back to
  // its corporateID where that is empty (OFSQA and OFSSuite today). So target
  // this per brand on the corporate group, never with a distinctId rollout — a
  // percentage on distinctId splits one brand's members across both tab sets.
  // Mobile cannot import this enum (it sits outside the pnpm workspace) and
  // declares the key locally, as the guest-pass flag does — this entry keeps
  // the registry complete and is the name PostHog must carry.
  SeparateBookingTabs = 'separate-booking-tabs',
  // OC-454 — gates the weekly billing anchor: the anchor date input AND the billingDay weekday
  // selector it depends on. Target it per-tenant in PostHog; the anchor is inert without billingDay.
  BillingAnchorDateEnabled = 'billing-anchor-date-enabled',
  // CS — gates signed Media CDN video delivery per corporate. OFF ⇒ serve the raw
  // public upload (no HLS). Roll out per-brand once the CDN infra is live.
  ContentSuiteSignedCdn = 'content-suite-signed-cdn',
  // MEM-463 — rollback switch for video transcoding, per corporate. ON ⇒ the
  // video-transcode-start/complete Cloud Functions (Pub/Sub completion). OFF ⇒ the
  // consumer starts the job and polls it to completion over BullMQ, as before MEM-463.
  ContentSuiteVideoCloudPipeline = 'content-suite-video-cloud-pipeline',
  // MEM-362 — enforces the Experience-theme entitlement check per corporate. OFF ⇒ every
  // corporate is treated as theme-ACTIVE (the deferred-tiers behaviour agreed with Jarron).
  // Turn ON per corporate to bring the paid-addon gate back without a deploy.
  ContentSuiteExperienceThemeGate = 'content-suite-experience-theme-gate',
  // MEM-235 — hide unreleased Core Content Suite features. All default OFF (fail closed).
  // Brand Only (core-brand-only-enabled) intentionally deferred — no UI to gate yet.
  CoreContentVersioningEnabled = 'core-content-versioning-enabled',
  CoreScheduleButtonEnabled = 'core-schedule-button-enabled',
  CoreTagsClassificationEnabled = 'core-tags-classification-enabled',
  CoreAudioArticleEnabled = 'core-audio-article-enabled',
  // Also read by the member app (mobile useChallengesEnabled): one flag governs
  // the Core authoring UI and every member-facing challenge surface together, so
  // turning it off to hide unfinished admin UI also hides Challenges on phones.
  CoreChallengesEnabled = 'core-challenges-enabled',
  // MEM-408 — member app only: the Withdraw button on the challenge detail screen.
  // The server accepts a withdraw regardless; this is the rollout switch for the UI.
  ChallengeWithdrawEnabled = 'challenge-withdraw-enabled',
  CoreAwardEnabled = 'core-award-enabled',
  // MEM-613 — which milestone grants members see. Both are always written (site grants in
  // CmsClientSiteAward, corporate grants in CmsClientAward); ON ⇒ the awards API shows the
  // corporate ones, OFF ⇒ the site ones (DEFAULT_MILESTONE_SCOPE, Location).
  CoreMilestoneCorporateEnabled = 'core-milestone-corporate-enabled',
  // Also read by the member app (mobile useMetricsEnabled) for the CMS-home
  // metrics promo card — the only entry point into the metrics flow.
  CorePerformanceMetricsEnabled = 'core-performance-metrics-enabled',
  // MEM-611 — gates the "Rating Metric" Metric Type in the Content Suite editor (J.I.M.
  // performance self-assessment) and the corresponding mobile Performance Tracker rating
  // flow. Default OFF (fail closed): existing metrics/entries unaffected either way.
  CoreRatingMetricEnabled = 'core-rating-metric-enabled',
  // Gates the OPEN_CONTENT + PLAY_VIDEO link-action choices in the banner link editor.
  CoreLinkOptionsEnabled = 'core-link-options-enabled',
  // Gates the Carousel/Banner Group creatable-autocomplete field in the banner editor.
  CoreCarouselGroupEnabled = 'core-carousel-group-enabled',
  // Gates the whole "Content & Media" content-type section (picker group + filters).
  CoreContentMediaEnabled = 'core-content-media-enabled',
  // MEM-327 — gates the Media Library "Add new media" multi-file staging/upload
  // path. Falls back to the existing single-file flow when off.
  CoreBulkMediaUploadEnabled = 'core-bulk-media-upload-enabled',
  // MEM-327 — gates the "attach external video URL directly" toggle in the
  // media picker (skips the Media Library detour). Upload-only when off.
  CoreMediaPickerExternalUrlEnabled = 'core-media-picker-external-url-enabled',
  // MEM-89 — gates the Zoom integration staff surfaces (settings connect form +
  // per-session meeting actions). Member-facing join URLs are unaffected.
  ZoomIntegrationEnabled = 'zoom-integration-enabled',
  // OC-320 — master safety gate for product tiering. Off/undefined/no-client/error
  // all resolve inert; see shared/backend/services/entitlements/product-tiering-flag.ts.
  // Per-corporate rollout (Elite Fit first, then widen) is configured in this flag's PostHog rules.
  ProductTiering = 'product-tiering',
  // INT-512 — gates the eGym integration tile (member sync config + Sync-all + CSV export)
  // in Location Settings → Integrations. Off until a site is being onboarded to eGym.
  EgymIntegrationEnabled = 'egym-integration-enabled',
  // Fitness Passport — the single kill switch for the whole integration. It gates BOTH the
  // backend call path (the grant at check-in, the record call, the retry consumer) and every
  // staff surface (the Integrations tile, the client-file field, the FP columns and report).
  // One switch is one switch: flag and per-site enablement must never gate different halves,
  // or the backend bills a partner for visits no operator can see. Evaluated fail-closed via
  // isFeatureFlagEnabled on the backend and useFeatureFlag on the frontend, so off, missing
  // and PostHog-unreachable all read false.
  FitnessPassportIntegrationEnabled = 'fitness-passport-integration-enabled',
  // OC-35 — gates the Team View time-clock surface (header self clock-in/out toggle,
  // per-row manager clock action, and the clock modal). Off ⇒ no clock UI is rendered.
  TimeClockEnabled = 'time-clock-enabled',
  // OC-838 — gates fee-inclusive partial gateway refunds on Ezypay invoices (item-level
  // refund of whole fee items alongside a base partial). OFF ⇒ partial refunds stay
  // capped at the base amount; the clearer cap error message ships unflagged.
  EzypayFeeInclusivePartialRefunds = 'ezypay-fee-inclusive-partial-refunds',
  // OC-969 — shows Ezypay's scheduled failed-payment fee as an estimate under the Amount of
  // failed Ezypay invoices on Failed Payments; the fee only lands on the invoice once Ezypay settles.
  EzypayFailedPaymentFeeEstimate = 'ezypay-failed-payment-fee-estimate',
  // OC-1971 — shows Ezypay's estimated load and transaction fees as their own rows in the
  // checkout summary and adds them to the displayed total (Core checkout and the legacy
  // payment info used by embed, kiosk and the member app). Display only: the amount sent to
  // Ezypay never changes, because Ezypay adds the fees itself. Read fail-CLOSED: OFF => no
  // estimate rows. Create it in PostHog (active, 0%) before this deploys.
  EzypayCheckoutFeeEstimate = 'ezypay-checkout-fee-estimate',
  // INT-139/INT-141 — gates every Wellhub staff surface: the web/core integrations
  // tile, status dialog, product mapping, schedule badge, schedule filter and the
  // session-form aggregator toggle, plus the web/admin divergence check. Turning it
  // off must clear all of them together, or staff keep seeing Wellhub state they
  // have no remaining control over. It does NOT stop ingress, the catalog workers
  // or any resolver — the runtime kill switches are AggregatorProvider.isActive and
  // AggregatorVenueIndex.isEnabled.
  WellhubIntegrationEnabled = 'wellhub-integration-enabled',
  // OC-1819 — shows the per-location "auto-assign a bed to ClassPass bookings" switch under
  // Location settings → Integrations. Gates the switch only: the gateway always honours the
  // stored AggregatorVenueIndex.autoAssignSpot, which stays on until a location turns it off.
  ClassPassAutoAssignSpotSetting = 'classpass-auto-assign-spot-setting',
  // OC-816 / OC-1502 — gates every custom session fee surface: custom no-show AND custom
  // late-cancel fees, plus the "No fee" option on templates, sessions and recurring
  // sessions (the Retail Fees switch and panels, the assignment selects, and the No-Shows
  // and Late Cancels report columns). OFF ⇒ fee resolution ignores CUSTOM and NONE
  // entirely and every no-show / late cancel falls back to the location's general fee, so
  // flipping it off mid-flight can never strand a charge. Key name predates OC-1502 and
  // stays: renaming a live PostHog flag needs a new flag plus a brand backfill.
  CustomNoShowFeesEnabled = 'custom-no-show-fees-enabled',
  // MEM-69 — gates the "Streak" option in the Awards editor Award Type dropdown until
  // the Streak award type is ready. OFF ⇒ the Streak option is hidden from the dropdown
  // on new awards; existing STREAK entries keep their value so their label still resolves.
  // MEM-96 — also read by the member app (mobile useStreaksEnabled) for the whole
  // weekly-streaks surface: the Home StreakCard, the streaks and tier-progress routes,
  // and the useStreaks query. One flag covers authoring the award type and displaying
  // the streak, so turning it off hides both. Mobile reads it fail-CLOSED.
  CoreAwardStreakEnabled = 'core-award-streak-enabled',
  InvitePartyEnabled = 'invite-party-enabled',
  // OC-772 — gates the Retail Settings → Add-ons section (list, form, archive).
  // Distinct from the legacy site-setting enableAddOns ("Add-on pricing", Tan Republic).
  AddOnsManagementEnabled = 'add-ons-management-enabled',
  // INT-705 — gates the Control Center → API and Integrations → API keys tab, where a
  // brand issues, renames, rotates, re-scopes and revokes its own public-API keys. OFF ⇒
  // the tab is not rendered and its route falls back to the default tab; the control-plane
  // resolvers stay reachable, so this is a UI gate, not an authorization boundary.
  // Created in the dev / staging / prod PostHog projects (100% / 0% / 0%).
  ApiKeysEnabled = 'api-keys-enabled',
  // OC-134 — gates site-configurable refund reasons: the Location Operations settings
  // card and the reason combobox in the refund modal. OFF ⇒ the refund modal keeps its
  // hardcoded reason list, so the flag doubles as the rollback switch.
  RefundReasonsEnabled = 'refund-reasons-enabled',
  // OC-882 — lets staff cancel an invoice stuck in `processing` from the payment detail
  // page. OFF ⇒ cancel stays limited to the statuses it already covered (scheduled,
  // staged, requires_action, failed_retry, failed); no other affordance changes.
  CancelStuckProcessingInvoice = 'cancel-stuck-processing-invoice',
  // OC-93 — gates the "Waitlist promotion method" control (Automatic / Confirmation
  // window) and its confirmation-window duration on the scheduler waitlist promotion
  // settings modal. OFF ⇒ the modal keeps only the existing promotion-window field.
  WaitlistPromotionMethodEnabled = 'waitlist-promotion-method-enabled',
  // OC-895 — gates the self-service Terminal hardware shop (Location settings → Retail →
  // Terminal → Hardware shop). Stripe-only. The components are a Stripe preview, now enabled
  // for both Hapana platform accounts; this stays OFF until hardware ordering is configured
  // in the Stripe Dashboard's Connect settings and the reader catalogue has been restricted.
  TerminalHardwareShopEnabled = 'terminal-hardware-shop-enabled',
  // OC-898 — gates the Capital surface (location left nav → Capital, and the page's Stripe
  // embedded financing components). Stripe-only, and the components are still a Stripe preview,
  // so this stays OFF until the Capital programme is live in the Stripe Dashboard.
  CapitalEnabled = 'capital-enabled',
  // OC-652 — gates the Brand Registry Configure API editor (web/core) so a brand's
  // server-side mobile config can be edited/published; kill-switch per-site if it misbehaves.
  ConfigureApiEnabled = 'configure-api-enabled',
  // OC-652 — gates the native build controls on the Mobile App page (readiness checklist,
  // "Build" button, store-verdict recording). Separate from ConfigureApiEnabled because
  // editing config is free while a build costs money and a store review slot, so the two
  // roll out independently and this one can be killed without disabling the editor.
  NativeReleaseControlsEnabled = 'native-release-controls-enabled',
  // Gates the Reports > Data Catalog tab (the Cube measure browser). Default OFF
  // — the surface is built and kept, but hidden until it is ready to show.
  DataCatalogEnabled = 'core-data-catalog-enabled',
  // Gates authoring brand-new insights from scratch (the free-form Cube widget
  // creator). Default OFF — the surface is built and kept, but hidden until it
  // is ready to show. Adding an EXISTING library insight to a dashboard is a
  // separate capability and is not gated by this.
  CustomInsightsEnabled = 'core-custom-insights-enabled',
  // OC-918 — gates Topaz SigWeb signature-pad capture on the public agreement signing
  // page. Checked server-side when building AgreementPublicView, alongside the site's
  // Topaz integration toggle (Site.integrations.$.topaz.enabled) — both must be on.
  // OFF ⇒ the page keeps the mouse/touch canvas everywhere.
  TopazSignaturePadEnabled = 'topaz-signature-pad-enabled',
  // MEM-301 — gates the fob credential-format surface end to end: the Admin location
  // configuration row/modal, its re-derivation endpoints, and Core's scan-to-enroll fob
  // UX with collision reassign. OFF ⇒ the Admin row is hidden and Core keeps the legacy
  // manual fobNumber field. Door-access surface, so this is the per-site kill switch.
  FobCredentialFormatEnabled = 'fob-credential-format-enabled',
  // INT-1507 — gates registering a member's fob from a paired Check-In Agent: Core's Terminal
  // pill and the request that arms the terminal. Separate from FobCredentialFormatEnabled so the
  // terminal path can be switched off at a site without taking its fob enrolment down with it.
  FobTerminalRegistrationEnabled = 'fob-terminal-registration-enabled',
  // OC-652 — gates the Apple compliance panel on the Mobile App page: membership expiry,
  // agreement state and certificate expiry. Separate from NativeReleaseControlsEnabled because
  // this is the only part of the page that depends on App Store Connect answering us, and a bad
  // key or an Apple rate limit should be able to hide the panel without taking the build button
  // down with it.
  AppleComplianceChecksEnabled = 'apple-compliance-checks-enabled',
  // OC-652 — gates the Setup tab on the Mobile App page: the onboarding checklist that mirrors
  // the mobile brand onboarding doc. Separate from the other two because it is staff-only
  // scaffolding for a brand that is not live yet, while those gate surfaces a live brand uses.
  MobileSetupGuideEnabled = 'mobile-setup-guide-enabled',
  // OC-100 — gates the per-site Block/Unblock and the irreversible Delete (anonymise) client
  // actions, in both the clients-table row menu and the client-profile header menu. Retrofitted:
  // the feature reached prod on 2026-07-31 without a flag, while the ticket was still In QA, and
  // was noticed live before QA had signed it off. OFF ⇒ neither action is offered, which is also
  // the rollback switch. Reads fail-closed via `useIsFeatureFlagEnabled`, so an absent, warming-up
  // or unreachable flag hides the actions rather than exposing them. This is a UI gate only — the
  // `blockClientAtSite` / `unblockClientAtSite` / `anonymiseClient` resolvers stay reachable and
  // remain permission-gated server-side, so it is not an authorization boundary. The legacy
  // reactivate-only restore action is deliberately NOT gated by this flag.
  ClientBlockAndDeleteEnabled = 'client-block-and-delete-enabled',
  // OC-167 — gates the Pay Up Front pre-sale UI: the template toggle in Retail Settings →
  // Memberships and the per-sale toggle in the New Sale membership dialog. OFF ⇒ neither
  // toggle renders and sales behave exactly as Phase 2 (first invoice charged on the opening
  // day). UI gate only — the backend clamp and charge path stay permission-gated server-side.
  PayUpFrontEnabled = 'pay-up-front-enabled',
  // OC-269 — gates Early Access on pre-sale membership templates: the template fields, the
  // checkout timeline and the $0 Early Access invoice. OFF ⇒ templates cannot gain Early Access
  // and sales of an Early Access template sell as a normal pre-sale membership.
  EarlyAccessEnabled = 'early-access-enabled',
  // OC-1235 — gates Pay Up Front for PACKAGE templates end to end: the Retail Settings → Packages
  // template toggle, the New Sale package-dialog toggle, the legacy member-API exposure, AND the
  // backend client-caller derivation (a template can be flagged over the API regardless of UI, so
  // this is a money gate, not a UI gate). Resolved with the fail-closed helper. Separate from
  // PayUpFrontEnabled so Vaura can be switched on while memberships are still in QA.
  PackagePayUpFrontEnabled = 'package-pay-up-front-enabled',
  // OC-1178 — gates a package template's fixed credit window end date end to end: the Retail
  // Settings → Packages field, the template write, and the sale path that pins every sale's credits
  // to that date. OFF ⇒ the field is hidden, cannot be saved, and a saved date is ignored at sale.
  PackageCreditWindowEnabled = 'package-credit-window-enabled',
  // OC-1992 — gates a package or membership template's sale availability dates end to end: the
  // Retail Settings fields, the Access Policy override permission row, the Core New Sale labels,
  // and every server check that refuses a sale outside the dates (payments and the V1 bridge).
  // OFF ⇒ the fields are hidden and saved dates are ignored everywhere. Fail-closed on the server.
  PackageSaleAvailabilityEnabled = 'package-sale-availability-enabled',
  // OC-1991 — gates a package or membership template's New Sale Dynamic Display list end to end:
  // the Retail Settings toggle and list, the Access Policy override permission row, the Core New
  // Sale greying, and every payments check that refuses a staff sale to a client who has not bought
  // a listed plan. OFF ⇒ the section is hidden and a saved list is ignored. Fail-closed on the server.
  NewSaleDynamicDisplayEnabled = 'new-sale-dynamic-display-enabled',
  // INT-1406 — gates the kiosk "change spot" button on a booking that is already checked in, for
  // every booking on the roster. OFF ⇒ a checked-in member's spot renders as the static badge,
  // exactly as before. UI gate only: the `/updateResourceBooking` write path never reads booking
  // status, and is the same one Core's Booked tab already uses to move attended members.
  KioskSpotChangeAfterCheckinEnabled = 'kiosk-spot-change-after-checkin-enabled',
  // OC-557 — gates the Retail Settings → Memberships "Active" member-count column and its
  // drill-through list of active members (name, start/end date, credits used, credit log).
  // OFF ⇒ the column is hidden and the memberships list renders exactly as before; the
  // count query never fires. Per-site rollout of a support/product convenience surface.
  MembershipActiveMembersEnabled = 'membership-active-members-enabled',
  // OC-1837 — Active | Expired | All tabs and inline purchased/used/unused credits in that
  // drill-through. OFF ⇒ the dialog lists active members only, exactly as before.
  MembershipMembersStatusFilterEnabled = 'membership-members-status-filter-enabled',
  // MEM-394 — Content Library, keyed on corporateId. This is the kill switch: OFF ⇒ no
  // catalogue is provisioned and no stream is minted for that brand. Deliberately separate
  // from the sync flags below so a bad mapper can be stopped without revoking the add-on.
  ContentLibraryEnabled = 'content-library-enabled',
  // Pauses catalogue ingestion from the provider without a deploy.
  ContentLibraryCatalogSync = 'content-library-catalog-sync',
  // Pauses provisioning and entry sync independently of ingestion, so catalogue freshness
  // is not lost while a mapping problem is fixed.
  ContentLibraryEntrySync = 'content-library-entry-sync',
  // Gates forwarding playback events upstream. These events are what the provider bills
  // from, so turning this off stops their meter, not ours.
  ContentLibraryPlaybackForwarding = 'content-library-playback-forwarding',
  // Phase-two product-led surface: in-platform purchase and self-serve location management.
  ContentLibrarySelfServe = 'content-library-self-serve',
  // OC-863 — gates the Retail Settings → General settings → Suspensions sub-tab, whose only
  // control is the per-site "secondary fees continue billing during suspensions" toggle. OFF ⇒
  // the sub-tab is not offered and its route falls back to the default tab. UI gate only: the
  // `updateSiteSettingsRetailSuspensions` resolver stays reachable and permission-gated
  // server-side, and the stored setting keeps whatever value it already has. The kill switch for
  // the BEHAVIOUR is the off-by-default site setting itself, not this flag.
  SuspensionSettingsEnabled = 'suspension-settings-enabled',
  // OC-862 — gates the RECURRING charge option in the pause dialog, and with it the fee-count
  // preview, since that only renders once RECURRING is selected. OFF ⇒ staff see only the existing
  // No fee / Flat options and no suspension can be created with a recurring fee. Enforced on the
  // BACKEND too, since chargeMode arrives over the API regardless of what the dialog offers — but
  // on CREATION only: an existing suspension keeps billing and terminating its fee on its own
  // terms, so turning this off never strands a fee that is mid-pause.
  SuspensionRecurringFeeEnabled = 'suspension-recurring-fee-enabled',
  // OC-295 — gates PRORATED suspensions (grid-aligned cycle shift + suspension credit). Unlike the
  // two flags above this is a BACKEND gate, not a UI one: `suspendService` accepts `suspensionType`
  // over the API, so no UI is needed to reach the path and a UI-only gate would leave it open. OFF
  // ⇒ a PRORATED request is rejected outright and every suspension is TIME_SHIFT. The check is
  // fail-closed, so an unreachable PostHog also means off. It gates CREATION only — an existing
  // PRORATED row keeps resuming on its own terms, so turning this off never strands a live pause.
  ProratedSuspensionsEnabled = 'prorated-suspensions-enabled',
  // OC-164 — gates the charge-or-absorb prompt shown when a collected pre-sale's opening date
  // moves EARLIER, leaving the member underpaid. OFF ⇒ no prompt and no charge; the shortfall is
  // absorbed silently, which is today's behaviour. The overpaid direction is automatic either way
  // and is never gated by this flag.
  PresaleShortfallPromptEnabled = 'presale-shortfall-prompt-enabled',
  // OC-915 — gates the "Pending" client status: a membership/package bought with a future
  // start date (Service.status PENDING_START_DATE) that has not begun yet. ON ⇒ such a client
  // reads as Pending instead of Membership/Package/Intro, and the Membership/Package/Intro and
  // Client State = Active filters stop returning them. OFF ⇒ the OC-478/OC-666 behaviour is
  // kept exactly: pending-start counts as live everywhere. Per-site, because it changes what
  // "active member" means on the clients list for every brand, not only pre-sale ones.
  ClientPendingStatusEnabled = 'client-pending-status-enabled',
  // OC-2050 — gates the Clients page "Profile created" filter and the sortable Created column.
  // UI gate only: the server accepts the filter either way, so a Smart Group saved while the
  // flag was on keeps filtering after it is turned off.
  ClientsProfileCreatedEnabled = 'clients-profile-created-enabled',
  // Gates the synchronous finalise step on the client-profile Documents tab. ON => after the
  // signed-URL PUT the client calls `completeClientFileUpload` and reports the real outcome.
  // OFF => the upload is reported successful as soon as GCS accepts the bytes and finalisation
  // is left to the object-finalize Cloud Function, which is the pre-existing behaviour and the
  // rollback switch. The Cloud Function runs either way and is idempotent, so flipping this
  // flag in either direction never strands or double-writes a file.
  ClientDocumentsDirectUploadEnabled = 'client-documents-direct-upload-enabled',
  // OC-652 — gates the mobile feature toggles on Member experiences → General Settings →
  // Advanced (call us, door check-in, dynamic images, news feed, challenges, awards and the two
  // app tabs). OFF ⇒ the section is hidden and the tab renders exactly as before. These write
  // brand config a member's app reads, so a wrong value is visible to members rather than staff;
  // the flag is the per-brand kill switch. Reads fail-closed, so an absent or unreachable flag
  // hides the section.
  MemberExperienceMobileTogglesEnabled = 'member-experience-mobile-toggles-enabled',
  // OC-652 — gates the Dark Mode strategy control on the Colors tab (Follow device / Always
  // light / Always dark). OFF ⇒ the control is hidden and the stored strategy is left alone,
  // which keeps every brand on the `auto` they ship with today. Separate from the toggles flag
  // because this one changes what members see the moment it is saved, colours included.
  MemberExperienceDarkModeEnabled = 'member-experience-dark-mode-enabled',
  // OC-652 — gates the per-surface Facebook credentials on the Advanced tab. OFF ⇒ the section
  // is hidden and the legacy single credential set on MemberExperienceSettings is left
  // untouched, so the widget keeps whatever it has. Separate flag because a wrong app id breaks
  // Facebook login for members on that surface.
  MemberExperienceSurfaceIntegrationsEnabled = 'member-experience-surface-integrations-enabled',
  // Gates every Book a Buddy UI surface: Core's buddy relations (unified Relations table BUDDY
  // entries, add/edit buddy dialogs) and the member app's buddy flows. Book a Buddy shipped long
  // before this flag, so it is a per-site/brand kill switch and every gate fails OPEN — an
  // absent, warming-up, or unreachable flag keeps the live feature visible.
  BookABuddyEnabled = 'book-a-buddy-enabled',

  // OC-132 — dark-launches the Session Feedback question builder (Location Settings →
  // Location operations, row 2.10). New surface, shipped as part 1 of a 3-part stack, so it
  // gates OFF by default until the brand opts in and the full stack has landed.
  SessionFeedbackConfigEnabled = 'session-feedback-config-enabled',
  // OC-1076 — gates the Schedule → Session Details expanded (full-screen) view: the header
  // expand/compress toggle and the wider modal layout (full detail grid, standalone Message /
  // Check-in all buttons, and the split Name/Alerts/Badges/Payment table columns). OFF ⇒ the
  // expand icon is hidden and the docked drawer renders exactly as before, so the flag doubles
  // as the rollback switch. Reads fail-closed via `defaultValue: false`.
  SessionDetailsExpandedViewEnabled = 'session-details-expanded-view-enabled',
  // OC-616 — gates the visibility of the terminal-failed-payment credits-on-hold toggle on
  // Retail Settings → General → Credits. The backend honours the site setting unconditionally;
  // this flag only controls UI discovery, so reads fail closed (absent flag hides the toggle).
  TerminalFailedPaymentHoldSetting = 'terminal-failed-payment-hold-setting',
  // OC-862 — gates composing several non-overlapping pauses in one submission ("Pause #N" plus
  // "+ Add a pause"). Enforced on the BACKEND as well as in the dialog, since the batch mutation
  // accepts an array of pauses over the API: with it OFF a batch of more than one pause is refused
  // outright and the dialog stays the single-pause form it is today. Gates CREATION only — pauses
  // already queued keep activating and resuming on their own terms.
  MultiplePausesEnabled = 'multiple-pauses-enabled',
  PreSalePauseEnabled = 'pre-sale-pause-enabled',
  // OC-862 — gates the read-only "Pause summary" financial preview in the pause dialog. UI gate
  // only; no backend path depends on it. OFF ⇒ the dialog keeps the two inline captions (the
  // prorated credit note and the secondary-fee note) that are today's shipped behaviour, so the
  // flag is also the rollback switch and turning it off never hides the credit entirely.
  SuspensionPauseSummaryEnabled = 'suspension-pause-summary-enabled',
  // MEM-556 — gates the mobile "this location has moved to our new experience" banner shown
  // on Home when the member's currently-active V1 site is detected mid-session (not just at
  // relaunch) to have migrated to V2. OFF => no banner; useRelaunchTokenExchange does not
  // auto-switch V1->V2, so a migrated member stays on the stale V1 session until they
  // manually re-select the site. New user-facing surface, so rollout is per-site through this
  // flag rather than shipping unconditionally.
  SiteMigratedBannerEnabled = 'site-migrated-banner-enabled',
  // OC-1049 — gates the Push tab in web/admin (nav item and every route under /push). OFF ⇒ the
  // nav item is absent and the route renders Access denied, so the portal can be pulled without a
  // deploy. Reads fail CLOSED: an absent or unreachable flag hides the tab, because the portal
  // exposes the whole push fleet's configuration to anyone the flag lets in.
  PushPortalEnabled = 'push-portal-enabled',
  // OC-1048 — gates the Push portal's Migration screen and tab and every push.* migration
  // procedure. The backend gate fails OPEN, so create this flag in the target PostHog project
  // BEFORE deploying.
  PushMigrationMonitorEnabled = 'push-migration-monitor-enabled',
  // OC-615 — gates the per-tier advanced booking window: the membership form/view field and the
  // member's booking-window bonus in session availability. OFF ⇒ every member resolves to the
  // site baseline, exactly as on main. The backend gate fails OPEN, so create this flag in the
  // target PostHog project (active, 0% rollout) BEFORE deploying.
  AdvancedBookingWindowEnabled = 'advanced-booking-window-enabled',
  // OC-1494: rejects member bookings (embed/app checkout, bookSession, waitlist joins) of sessions
  // not yet released by the rolling or timed release window. Checked fail-closed, so an unreachable
  // PostHog keeps today's behaviour of accepting them. Staff bookings are never restricted.
  MemberBookingWindowEnforcementEnabled = 'member-booking-window-enforcement-enabled',
  // OC-1056: gates the reservation-reminder "Send email hours before" and "Email calendar
  // attachment" fields on the Brand and Location communication-template editors. Lets the
  // configurable-timing + .ics feature roll out per-site and be turned off instantly.
  ReservationReminderConfigEnabled = 'reservation-reminder-config-enabled',
  // MEM-668 — gates the Content Suite reports (engagement, challenges, metrics, awards, video) in
  // the Reports library. OFF => the reports are hidden, cannot be deep-linked, run or exported. The
  // backend gate fails CLOSED for these reports, so they stay hidden until the flag exists and is
  // on for the brand.
  ContentSuiteReportsEnabled = 'content-suite-reports-enabled',
  // MEM-668 — a playback heartbeat that reaches 90% of a video marks it complete for the member.
  // Reads fail CLOSED, so the behaviour stays off until the flag is on for a brand.
  ContentVideoAutoCompleteEnabled = 'content-video-auto-complete-enabled',
  // OC-1137 — gates the optional "notify members" step on pre-sale opening-date moves, in both
  // the date dialogs and the backend send. The date move itself is never gated.
  MembershipUpdateCommsEnabled = 'membership-update-comms-enabled',
  // OC-1388 — gates Assigned Staff end to end: the Assigned staff card and its Edit/History
  // drawers on Client information, the assignClientSiteStaff mutation, and the seeding of the
  // field from a CORE membership/package sale. Reads fail CLOSED: an absent, warming-up or
  // unreachable flag hides the card, refuses the mutation and skips seeding, so with the flag
  // off nothing is ever written and there is no history to unwind.
  AssignedStaffEnabled = 'assigned-staff-enabled',
  // OC-278: superseded by PromoAfterIntroOfferEnabled — the PostHog copy of this flag was created
  // with a corporate-group release, which the backend's site-group evaluation can never match, so
  // it showed the Core checkbox while silently skipping enforcement. Nothing reads this key any
  // more; it stays registered only because the flag still exists in PostHog. Do not wire it back.
  ExistingClientsOnlyPromoEnabled = 'existing-clients-only-promo-enabled',
  // OC-278: gates the "Available after intro offer" promo-code setting — the Core checkbox and
  // the view-dialog row, and the validator gate itself. Enforcement is gated too, not just the
  // control: CSV import writes the column without consulting this flag, so hiding the checkbox
  // alone would leave a live checkout restriction Core could neither show nor clear. Turning it
  // on enforces existing-client-only on every code already carrying the column, imported ones
  // included; turning it off returns them to unrestricted without a deploy. Release it straight
  // on/off or by the site group — never by corporate group or percentage of users: the backend
  // evaluates with distinctId 'server' and only the site group, so any other condition reads
  // false at checkout while the Core screen shows the control.
  PromoAfterIntroOfferEnabled = 'promo-after-intro-offer-enabled',
  // OC-628 — gates the clickable session rows on the client profile's Schedule tab (Booked,
  // Past and Cancelled, desktop rows and mobile cards alike). OFF => the tabs pass no
  // onRowClick at all, so the pointer cursor, the click and the mobile card's button
  // semantics all disappear together and the tabs read exactly as they do today. Reads fail
  // closed, so the flag is also the rollback switch for a screen front-desk staff live in.
  ClientScheduleSessionDetailsEnabled = 'client-schedule-session-details-enabled',
  // OC-1374 — gates "pay at checkout" for an immediate membership upgrade: the pro-rated amount
  // becomes a staged invoice paid in the staff checkout, and the upgrade applies only when that
  // invoice is paid. OFF ⇒ the change plan dialog shows no collection choice and the backend
  // rejects `collectAtCheckout`, so the card on file path is the only one, exactly as on main.
  // Both reads fail CLOSED. Create the PostHog flag active at 0% rollout BEFORE deploying; an
  // inactive flag fails open.
  PlanChangePayAtCheckoutEnabled = 'plan-change-pay-at-checkout-enabled',
  // Gates the Location Operations "Lead sources" list (add/edit/remove) and the dynamic
  // "How did you hear about us?" dropdown it drives on both the staff add-client dialog
  // and self-serve sign up. OFF ⇒ the settings card is hidden and sign up keeps whatever
  // hardcoded enrollment-source list it has today.
  LeadSourcesEnabled = 'lead-sources-enabled',
  // INT-1066 — routes the client_and_attendance export SECTION (every report
  // whose cube lives under model/export/client_and_attendance: All Clients
  // today, First Visits etc. as they land) through the Cube semantic layer
  // instead of direct Spanner/BigQuery SQL. Cube-export flags are one per
  // model/export/<section> folder, never per report — a new section mints a
  // new <section>-cube-exports-enabled flag; a new report in an existing
  // section reuses this one. Per-request: even with the flag ON, a request
  // whose columns or filters the cube mapping cannot express falls back to
  // the legacy SQL path. Read through the fail-CLOSED gate in
  // cube-report-source.ts (not isFeatureFlagEnabled, which fails open): only
  // an explicit PostHog true routes to cube, so an outage keeps the legacy path.
  ClientAndAttendanceCubeExportsEnabled = 'client-and-attendance-cube-exports-enabled',
  // MEM-699 — gates brand Custom Text reaching the embed booking widget. OFF ⇒ /site/settings
  // omits the customText field entirely and never reads it, so every widget renders its
  // bundled copy and the response is byte-identical to before the feature.
  // Gated on the backend rather than in the widget because the widget's flag hook settles
  // asynchronously and would race the settings fetch it must precede. Reads fail CLOSED, so
  // this is also the instant rollback for a brand whose overrides render badly — the widget
  // is embedded on customers' own sites and cannot be hot-fixed.
  WidgetCustomTextEnabled = 'widget-custom-text-enabled',
  // OC-1529 — honours location.checkIn.checkInMarksAttendance. OFF ⇒ the setting is ignored and
  // passive scans mark booked classes attended as before; also hides the Core toggle.
  CheckInMarksAttendanceSettingEnabled = 'checkin-marks-attendance-setting-enabled',
  // OC-835 — offers "Suspend membership" in the client profile's package overflow menu, opening the
  // same pause dialog the Recurring Payment Detail screen does. Access point only: the pause itself
  // is still governed by ProratedSuspensionsEnabled / MultiplePausesEnabled. OFF => the menu reads
  // exactly as it does today and staff reach the dialog through See details.
  SuspendFromPackageMenuEnabled = 'suspend-from-package-menu-enabled',
  // OC-1578 — dark-launch gates for work that reached main before QA sign-off, so main can ship
  // to prod as-is. Every key is read fail-CLOSED: OFF, missing or unreachable restores the
  // behaviour prod ran before the originating ticket.
  // OC-855 — Retail Dashboard Gift Card tab sourced from the GiftCard table.
  GiftCardTableSourceEnabled = 'gift-card-table-source-enabled',
  // INT-1405 — Access log CSV export, search and sorting.
  AccessLogExportEnabled = 'access-log-export-enabled',
  // OC-853 — Custom Expiration credit-expiry option on memberships.
  MembershipCustomExpirationEnabled = 'membership-custom-expiration-enabled',
  // OC-1198 — holds a seat for a waitlist member while their invitation is pending.
  WaitlistInvitationSeatHoldEnabled = 'waitlist-invitation-seat-hold-enabled',
  // OC-282 — invoice tax calculation honours the site-level tax.enabled setting.
  SiteTaxToggleEnforcementEnabled = 'site-tax-toggle-enforcement-enabled',
  // MEM-689 — embed Simplified Checkout respects package purchase limits.
  EmbedCheckoutPurchaseLimitsEnabled = 'embed-checkout-purchase-limits-enabled',
  // OC-1220 — Core checkout recovers stale sale invoices on load and on payment failure.
  SaleCheckoutRecoveryEnabled = 'sale-checkout-recovery-enabled',
  // MEM-518 — phone-number plan validation on embed widget, dashboard and kiosk forms.
  EmbedPhoneValidationEnabled = 'embed-phone-validation-enabled',
  // MEM-347 — processing fee shown in legacy payment info for embed, kiosk and mobile.
  LegacyProcessingFeeDisplayEnabled = 'legacy-processing-fee-display-enabled',
  // MEM-698 — private sessions bookable without choosing a staff member.
  StafflessPrivateBookingEnabled = 'staffless-private-booking-enabled',
  // INT-939 — CSV client import fans imported members out to Grow and other integrations.
  GrowCsvImportSyncEnabled = 'grow-csv-import-sync-enabled',
  // INT-945 — extra Grow client-update publishes that close the audit's sync gaps.
  GrowSyncGapsEnabled = 'grow-sync-gaps-enabled',
  // MEM-606 / MEM-548 — Content Suite scheduled jobs newly wired into BullMQ, including
  // streak reconciliation and streak-at-risk member notifications.
  ContentSuiteScheduledJobsEnabled = 'content-suite-scheduled-jobs-enabled',
  // MEM-958 — gates the three leaderboard scheduled jobs (materialize-leaderboard-rankings,
  // create-leaderboard-snapshots, cleanup-leaderboard-entries) per brand. Read fail-CLOSED with
  // the corporate group only, so release it by corporate; OFF or unknown => the brand is skipped.
  ContentLeaderboardJobsEnabled = 'content-leaderboard-jobs-enabled',
  // MEM-958 — gates reconcile-streaks and notify-streak-alert per brand, separately from
  // core-award-streak-enabled (which shows the streak screens to members). Read fail-CLOSED with
  // the corporate group only; OFF or unknown => no streak resets and no streak-at-risk pushes.
  ContentStreakJobsEnabled = 'content-streak-jobs-enabled',
  // OC-121 — Guest Pass report, separate from the live guest-passes-enabled flag.
  GuestPassReportEnabled = 'guest-pass-report-enabled',
  // OC-1485 — branded email shell wraps authored bodies by default.
  BrandedEmailAlwaysWrapEnabled = 'branded-email-always-wrap-enabled',
  // OC-278 — promo codes enforce availability before an intro offer and staff discount authority.
  PromoIntroOfferEnforcementEnabled = 'promo-intro-offer-enforcement-enabled',
  // OC-1502 — custom late-cancel fees and the per-session "No fee" override.
  CustomLateCancelFeesEnabled = 'custom-late-cancel-fees-enabled',
  // OC-1398 — lets staff send an agreement for signature only, for a sale paid at the desk. The
  // signing link and the app then take no card. Reads fail CLOSED on the backend. OFF ⇒ every
  // send carries the payment step exactly as before.
  SendForSignatureOnlyEnabled = 'send-for-signature-only-enabled',
  // INT-1595 — lets a member spend account credit on an emailed signing link and on a Pay Up Front
  // charge collected at sign-up. Money gate, read fail-CLOSED on the backend. OFF => the signing
  // page shows no credit option and Pay Up Front charges the card in full, as before.
  AccountCreditAtSignUpEnabled = 'account-credit-at-sign-up-enabled',
  // OC-1238 — the deploy-independent kill switch for member self-cancellation. The Retail Settings
  // toggle is the business decision, this is the engineering one: the path changes eligibility and
  // raises fee invoices across every recurring membership, so a bad rollout must be stoppable
  // without a deploy. Read fail-CLOSED, so a PostHog outage leaves the site on its pre-OC-1238
  // behaviour rather than opening cancellation to everyone. OFF => the site toggle keeps its old
  // sold-online-only meaning and the member-facing surfaces stay hidden.
  ClickToCancelEnabled = 'click-to-cancel-enabled',
  ClientsSelectAllEnabled = 'clients-select-all-enabled',
  // INT-341 — Attendance by Session report (V1 parity).
  AttendanceBySessionReportEnabled = 'attendance-by-session-report-enabled',

  // OC-1222 — gates the access rule assignability UI (sectioned policy list, locked locations,
  // explanation panel), the two queries behind it (staffAccessPolicyAssignabilitySummary,
  // staffAccessPolicyAssignability), and the tiering exclusion in the assignability filters: with
  // it on, the four plan / add-on permissions no longer block assigning a policy. That last part
  // loosens an authorization check, so it is read fail-CLOSED: an absent, warming-up or unreachable
  // flag excludes nothing and refuses the queries. OFF => today's behaviour, MEM-597's UI included.
  AccessRuleAssignabilityEnabled = 'access-rule-assignability-enabled',
  // MIG-55 — gates moving a credit-paid booking's credit to another of the member's packages
  // (session Booked tab and Client > Schedule > Booked sessions). Read fail-CLOSED on both sides:
  // the change rewrites which package a booking is paid from, so an outage must hide it.
  BookingCreditPackageChangeEnabled = 'booking-credit-package-change-enabled',
  // INT-1412 — kill switch for delivering a brand's webhooks (the brand-wide subscriptions in
  // Developer APIs ▸ Webhooks; a location's own subscriptions are not gated). Per brand, on the
  // `corporate` group. Read fail-OPEN, so create the flag (targeting the launch brands) before
  // deploying; OFF ⇒ events go only to location subscriptions, as before INT-1412.
  BrandWebhooksEnabled = 'brand-webhooks-enabled',
  // INT-1412 — the Developer APIs ▸ Logging tab, the first surface showing (redacted) request and
  // response bodies. Read fail-OPEN like the delivery switch above; OFF ⇒ the tab and its Overview
  // card are hidden. Logging itself keeps running, so turning it back on loses no history.
  ApiCallLoggingEnabled = 'api-call-logging-enabled',
  // MEM-836 — Stripe Apple Pay on the member web add-card surfaces, on top of the per-site
  // applePayEnabled toggle. Read fail-CLOSED with the site group only, so release by site group.
  ApplePayEnabled = 'apple-pay-enabled',
  // MEM-978 — Stripe Google Pay in the Android member app's add-card sheet, on top of the per-site
  // googlePayEnabled toggle. Read fail-CLOSED with the site group only, so release by site group.
  GooglePayEnabled = 'google-pay-enabled',
  // MEM-849 — the "Apply discount to: first payment / all payments" choice on a membership sold
  // at New Sale. Read fail-CLOSED on both sides: OFF => the choice is hidden and a line discount
  // stays first-payment-only (the OC-190 behaviour), so the rollout can be stopped without a deploy.
  MembershipDiscountAllPaymentsEnabled = 'membership-discount-all-payments-enabled',
  // OC-1288: gates the "Cancel payment" action on a pending card or bank payment on the
  // invoice page. OFF ⇒ the action is not rendered and the mutation refuses server-side.
  PaymentCancelPendingEnabled = 'payment-cancel-pending-enabled',
  // OC-1294 — gates the root-mounted help-docs routes (/help-docs and /help-docs/:slug) that let
  // a brand-only KB link resolve to the reader's own site. OFF => help docs stay reachable only
  // under /c and /s/:siteId exactly as on main, and a root link redirects to the brand-level
  // equivalent (/c/help-docs/...) rather than the login bounce, which would loop for an already
  // authenticated user. Reads fail CLOSED. The articles.<root> redirector is deliberately not
  // gated (see PR): it is anonymous, so PostHog has no user or site to target.
  DynamicKbLinksEnabled = 'dynamic-kb-links-enabled',
  // OC-765 — gates Extend / Reactivate on a client's paid-in-full packages and memberships. Read
  // fail-CLOSED on both sides: reactivation flips an expired plan back to Active and restores the
  // credits that expired with it, so an outage must hide the action rather than offer it.
  PlanExtensionEnabled = 'plan-extension-enabled',
  // MEM-705 — resolves the 11 agreement shortcodes the server used to blank (CREDITS, EXP_DATE,
  // SALESPERSON, ...). Only agreements created on or after the cutoff in client-agreements are
  // affected, so an executed contract never changes under its frozen, hashed PDF. Read fail-CLOSED
  // with the site group; create it in PostHog (active, 0%) before deploying.
  AgreementFullShortcodeRendering = 'agreement-full-shortcode-rendering',
  // OC-1770 — gates the Member Experiences > Deeplinks tab and every deep-link GraphQL operation,
  // separately from self-service-enabled so deep links can ship per brand. Fails CLOSED on both
  // sides; create the flag in PostHog (active, 0% rollout) before deploying. Release it by the
  // corporate group only: the server evaluates it with the corporate group and no person, so a
  // person-targeted release shows the tab in Core while every call returns FEATURE_DISABLED.
  DeepLinksEnabled = 'deep-links-enabled',
  // INT-1783 — kill switch for payroll subtotals, which ship ON (V1 parity). The reporting gate
  // fails OPEN, so an absent flag leaves subtotals on; only an explicit OFF for a corporate or site
  // hides the Subtotals filter and returns the flat report and CSV.
  PayrollSubtotalsEnabled = 'payroll-subtotals-enabled',
  // OC-1573 — the Refunds permission (payments__refund). Read fail-CLOSED on both sides: while it is
  // off the policy editor hides the toggle and no refund checks the bit, so refunds behave exactly as
  // before. Create it in PostHog (active, 0%) before this deploys.
  RefundPermissionEnabled = 'refund-permission-enabled',
  // OC-1698 — tanning lamp hour tracking (Wellness > Manage equipments, Lamp hours dialog, lamp
  // columns on Equipment Usage). The backend gate on lamp writes reads fail-CLOSED; lamp status
  // reads are not gated, since they only return beds enabled through a gated write.
  TanningLampHourTracking = 'tanning-lamp-hour-tracking',
  // OC-1372 — gates reversing a tanning session (the "Move to another bed" action behind the
  // equipment menu's Reset slot): the frontend hides the dialog and the backend refuses the
  // mutation. OFF ⇒ Stop Timer is the only way to end a session early, exactly as on main. The
  // backend gate fails CLOSED too; create this flag in the target PostHog project (active, 0%
  // rollout) BEFORE deploying.
  TanningSessionReverseEnabled = 'tanning-session-reverse-enabled',
  // OC-1796 — gates voiding a tanning session without moving it (the "Void session" action next
  // to "Move to another bed"): the frontend hides the item and the backend refuses the mutation.
  // Kept apart from tanning-session-reverse-enabled so the two roll out independently. Fails
  // CLOSED; create this flag in the target PostHog project (active, 0% rollout) BEFORE deploying.
  TanningSessionVoidEnabled = 'tanning-session-void-enabled',
  // OC-1368: Reset on a tanning bed with a running client session restarts that session on the
  // same row (original delay and tan time resent to the agent, no new credit). ON ⇒ the menu
  // opens the "Reset bed time" dialog and the backend accepts restartEquipmentAssignment. OFF ⇒
  // Reset ends the session and marks the bed clean, exactly as on main. Fails CLOSED on both
  // sides; create the flag in the target PostHog project (active, 0% rollout) before deploying.
  TanningBedResetRestartEnabled = 'tanning-bed-reset-restart-enabled',
  // OC-1795: locks tanning Test Time to the bed's Minimum Time unless the user holds
  // wellness__equipment_test_time__override, and marks test fires in the equipment activity log.
  // Fails CLOSED on both sides (off = any time within the bed's bounds, as before); create the flag
  // in the target PostHog project (active, 0% rollout) before deploying.
  TanningTestTimeLock = 'tanning-test-time-lock',
  // OC-1562 — gates the "Only allow plan changes within the same membership category" setting on
  // the Membership tier order page and the server rule it drives. Read fail-CLOSED with the site
  // group: OFF => a saved setting does nothing, so Change Plan offers every category as before.
  MembershipPlanChangeByCategoryEnabled = 'membership-plan-change-by-category-enabled',
  // MIG-2 — gates Scheduler rules 4.1.8 and the booking overlap guard that reads it. Fail-closed
  // on the backend: with the flag off every booking keeps the OC-698 behaviour exactly.
  MemberOverlappingSessionsSetting = 'member-overlapping-sessions-setting',
  // MEM-744 — drag-to-reorder on the Core Schedule Templates list, and the widget/app privates list
  // following that order. The sites and api backend gates fail OPEN, so create the flag in PostHog
  // (active, 0% rollout) before deploying. OFF => no drag in Core, the reorder mutation is refused,
  // and privates stay alphabetical even for a site that was reordered while it was on.
  ScheduleTemplateReorderEnabled = 'schedule-template-reorder-enabled',
  // MEM-27 — the Cancel | Delete choice in the cancel-session dialog (V1 "Delete All Events"
  // parity). The dialog reads it fail-CLOSED; the sites backend gate fails OPEN, so the flag must
  // exist in PostHog (0% rollout) before this deploys or deleting is callable for every brand.
  ScheduleDeleteSessionsEnabled = 'schedule-delete-sessions-enabled',
  // OC-1836 — gates "Roll over credits" on an expired pack and the rollover mutation. Read
  // fail-CLOSED on both sides: a rollover moves credits between packs and cannot be undone from the
  // UI, so an outage must hide the action. Create it in PostHog (active, 0%) before this deploys.
  ExpiredCreditRolloverEnabled = 'expired-credit-rollover-enabled',
  // OC-143 — the read-only View details dialog on draft invoices (Retail dashboard and client
  // profile). Read fail-CLOSED: while it is off neither menu shows the action. Create it in PostHog
  // (active, 0%) before this deploys.
  DraftInvoiceViewDetailsEnabled = 'draft-invoice-view-details-enabled',
  // OC-1858 — the cancellation notice period on unending memberships: the template setting, the
  // cancel check and its preview. Read fail-CLOSED: OFF => no cancellation is held to a notice
  // period and stored values stay inert. Create it in PostHog (active, 0%) before this deploys.
  CancellationNoticePeriodEnabled = 'cancellation-notice-period-enabled',
  // BUG-598 — when Stripe refuses a site → Hapana fee debit for the site's country ("account
  // debits are not supported" / "reference_transaction is required"), take the fee as a partial
  // reversal of a charge transfer instead. Read fail-CLOSED: OFF ⇒ the refused debit stays failed.
  // Create it in PostHog (active, 0%) before this deploys.
  SiteFeeCrossBorderCollection = 'site-fee-cross-border-collection',
  // OC-2008: add-on price groups. Gates the Retail settings "Price groups" tab, the Add-on pricing
  // section in the membership and package dialogs, every group mutation (refused with
  // ADD_ON_PRICE_GROUPS_FEATURE_DISABLED) and the bed's credit order that prefers a covering credit
  // with no add-on owed. OFF => prices a group already wrote stay ordinary add-on prices and the bed
  // picks credits exactly as on main. Fails CLOSED on both sides; create the flag in the target
  // PostHog project (active, 0% rollout) before deploying.
  AddOnPriceGroups = 'add-on-price-groups',
  // OC-927 — lets a cardholder-present checkout (member app, embeds, public agreement pages)
  // keep a 3DS-challenged PaymentIntent alive and hand the client secret back for an inline
  // challenge. OFF => every path cancels on 3DS exactly as before (authentication_required_3ds_automated).
  // Reads fail CLOSED with the site group so it can be released per site.
  StripeInlineCardChallengeEnabled = 'stripe-inline-card-challenge-enabled',
  // INT-1372 — the Grow churn-risk tag. Read fail-CLOSED per corporate: OFF hides the admin
  // threshold field and evaluates as if no threshold were set, so existing tags are removed.
  GrowChurnRiskTagEnabled = 'grow-churn-risk-tag-enabled',
  // OC-945 — keeps a 3DS-challenged staff (Core) or opted-in kiosk charge held on the same
  // PaymentIntent so the member can approve it from an emailed link. OFF => those charges cancel on
  // 3DS exactly as before. Only entry into the held state is gated: a payment already held still
  // resolves through the approval page, webhook, release and expiry sweep with the flag off.
  // Reads fail CLOSED with the site group so it can be released per site.
  StripeRemoteCardChallengeEnabled = 'stripe-remote-card-challenge-enabled',
  // OC-148 — staff reinstating invoices they cancelled. Read fail-CLOSED on both sides: while it is
  // off cancels are not stamped as staff_manual, the Cancelled invoices tabs and the reinstate
  // permission toggle are hidden, and the reinstate mutations refuse. Create it in PostHog
  // (active, 0%) before this deploys.
  InvoiceReinstateEnabled = 'invoice-reinstate-enabled',
  // OC-1228 — agreement signatures collected on a Stripe reader (Settings › Retail › Terminals,
  // checkout, client Documents). Read fail-CLOSED on both sides: OFF => the hand-off mutations
  // refuse with TERMINAL_HANDOFF_FLAG_OFF and Core keeps today's on-screen and Topaz signing.
  // Create it in PostHog (active, 0%) before this deploys.
  TerminalSignatureCaptureEnabled = 'terminal-signature-capture-enabled',
  // OC-1859 — members completing missing profile details on a Stripe reader during a sale. Read
  // fail-CLOSED; only consulted when TerminalSignatureCaptureEnabled is also on for the site.
  TerminalFieldCaptureEnabled = 'terminal-field-capture-enabled',
  // OC-1230 — the "Awaiting signature / payment" reason on draft invoices (Retail dashboard,
  // client profile and the draft details dialog). Read fail-CLOSED; create it in PostHog
  // (active, 0%) before this deploys.
  DraftInvoicePendingReasonEnabled = 'draft-invoice-pending-reason-enabled',
  // OC-1953 — the Connector health card on Wellness settings › Manage equipments and the
  // tanningConnectorHealth query. Health recording, plain-words connector errors and the
  // diagnostics boolean fix are not gated. Read fail-CLOSED on both sides: OFF => the query refuses
  // with TANNING_CONNECTOR_HEALTH_DISABLED and the card stays hidden. Create it in PostHog
  // (active, 0%) before this deploys.
  TanningConnectorHealth = 'tanning-connector-health',
  // INT-1859 — rollout switch for GET /business/access/bookings, the partner bookings pre-load
  // feed. Fail-closed per corporate or site: OFF => the route answers 404 FEATURE_NOT_ENABLED, so
  // a key cannot pull a site's booking roster before the door-scope rule (INT-1856) lands. Create
  // it in PostHog (active, 0%) before this deploys and turn it on per Inner site at go-live.
  PartnerBookingsFeed = 'partner-bookings-feed',
  // OC-1847 — gates the revenue tag configuration surfaces (fee defaults, template overrides,
  // category / gift card / session selects, cart chip). Fail-closed; the invoice-item snapshot
  // itself is not flagged. Create it in PostHog (active, 0%) before this deploys.
  RevenueTagConfigEnabled = 'revenue-tag-config-enabled',
  // BUG-656 — the tax on refunds in reports: Tax Refunded and Net Tax on Net Revenue Detail (with
  // Net Revenue subtracting only the pre-tax part of a refund), refund rows and Tax Refunded on the
  // Tax Summary, and the tax line in the refund dialog. Read fail-OPEN on the server; OFF => the
  // reports keep today's columns and SQL. Create it in PostHog (active, 0%) before this deploys.
  RefundTaxReporting = 'refund-tax-reporting',
  // INT-1854 — "Open in Hapana" links under assistant answers. Read fail-CLOSED: OFF => answers
  // carry no destination links. Create it in PostHog (active, 0%) before this deploys.
  AssistantDestinationLinks = 'assistant-destination-links',
  // OC-1112 — gates restricting a no-show/late-cancel fee to bookings paid with unlimited
  // credits. Read fail-OPEN on the backend: with PostHog unreachable, ignoring the restriction
  // would charge the members an operator chose to exempt. OFF => the per-fee toggle is hidden
  // and every charge path behaves as it did before the restriction existed.
  UnlimitedOnlySessionFeesEnabled = 'unlimited-only-session-fees-enabled',
  // INT-1897 — Missing Payment Methods report (location + brand) and the migration utility's
  // "Open in Reports" link. Read fail-CLOSED, so the report stays hidden until PostHog explicitly
  // enables it for a corporate. Create it in PostHog (active, 0%) before this deploys.
  MissingPaymentMethodsReportEnabled = 'missing-payment-methods-report-enabled',
  // OC-1986 — brand option that blocks check-in, bookings and tans at every location while the
  // client owes at any location. Read fail-CLOSED: OFF => only the current location's failed
  // invoices are checked, as today. Create it in PostHog (active, 0%) before this deploys.
  OwedBalanceBlockEnabled = 'owed-balance-block-enabled',
  // Seasonal/themed loaders (Halloween, Christmas, ...) in the shared Loading component. Read
  // fail-CLOSED: anything but true renders the standard Hapana loader everywhere.
  SeasonalLoadersEnabled = 'seasonal-loaders-enabled',
  // INT-1903 — KPI Performance report (V1 parity, read from reporting_dwh through Cube). Read
  // fail-CLOSED; target it only at Pro brands while ProductTiering is not live for them.
  KpiPerformanceReportEnabled = 'kpi-performance-report-enabled',
  // OC-1810 — the member app sends its brandId, overlays the full runtime features map and takes
  // explore tabs at runtime; the gateway serves a provisioning brand only to a request naming it.
  // Mobile cannot import this enum and declares the key locally, as SeparateBookingTabs does.
  MobileRuntimeConfigByBrand = 'mobile-runtime-config-by-brand',
  // OC-1810 — a release request writes its marketing version to the Brand row. OFF ⇒ the version
  // lands on the release row only, exactly as before, so a bad version can be stopped per corporate.
  BrandReleaseVersionControlEnabled = 'brand-release-version-control-enabled',
  // OC-1810 — staff editors for build-profile fields and the update channel. Separate because a
  // wrong value ships in the next native build, so it needs its own kill switch.
  BrandBuildProfileEditorEnabled = 'brand-build-profile-editor-enabled',
  // OC-1810 — home logo and tab icon uploads and their release snapshot. OFF ⇒ builds keep the
  // folder or fleet artwork, so a bad upload can never reach a binary.
  BrandNavigationArtworkEnabled = 'brand-navigation-artwork-enabled',
  // OC-1812 — gates only Setup's automatic APNs key create in the brand's Apple team. The staff
  // "Upload APNs key" fallback stays ungated: it replaces a manual step that already exists.
  BrandApnsKeyAutoCreateEnabled = 'brand-apns-key-auto-create-enabled',
  // Registered so PostHog's name lives here; the member app declares it locally
  // (useMemberExperienceSync) because it cannot import this enum.
  MobileMemberExperienceSync = 'mobile-member-experience-sync',
  // Registered so PostHog's name lives here; the member app declares it locally
  // (useMemberExperienceRealtime) because it cannot import this enum.
  MobileMemberExperienceRealtime = 'mobile-member-experience-realtime',
  // PRD-1 A-2 (partner API parity) — the session.instructor_changed site-webhook event, the
  // instructor and changed-field additions on session.updated, and the event in the Core
  // webhook picker. Read fail-CLOSED on both sides, so subscribers see nothing new until it is on.
  PartnerInstructorWebhooksEnabled = 'partner-instructor-webhooks-enabled',
  // Native class covers (docs/native-class-covers/PRD.md) — the request / claim / approve cover
  // workflow on top of substitution. The web app reads it fail-CLOSED; the sites backend gate fails
  // OPEN, so the flag must exist in PostHog (0% rollout) before this deploys. OFF => no cover surface
  // renders and covers operations are refused with CLASS_COVERS_DISABLED; direct substitution is
  // untouched.
  ClassCoversEnabled = 'class-covers-enabled',
  // OC-1815 — gift card expiry. A kill switch and rollout control only: whether a card may expire
  // is decided by the Hapana-managed jurisdiction rules in the admin portal, which default to
  // "not permitted". Read fail-CLOSED on both sides with the corporate and site groups. OFF hides
  // the template and sale controls, issues every new card without an expiry, stops the daily sweep
  // and stops rejecting a card whose date has passed but has not been swept. A card the sweep has
  // already written off stays expired; the backend still lets staff remove its expiry to reinstate
  // it, but not set a new date. Create it in PostHog (active, 0%) before deploying.
  GiftCardExpiryEnabled = 'gift-card-expiry-enabled',
  // OC-1721 — sales commissions. Kill switch for every commissions surface and the event worker; the
  // brand's own on/off history lives in CommissionEnablement. Read fail-CLOSED everywhere, and an
  // unknown PostHog state never skips an event.
  CommissionsEnabled = 'commissions-enabled',
  // docs/ip-based-user-restrictions — Allowed Networks module, network assignment on team members
  // and access policies, and gateway enforcement. Enforcement treats only an explicit OFF as off:
  // a PostHog outage must never lift a brand's IP restrictions.
  StaffIpRestrictionsEnabled = 'staff-ip-restrictions-enabled',
  // docs/ip-based-user-restrictions §8 — brand-level staff session policy (brand-page idle timeout,
  // location override rule, gateway-enforced maximum session length) and the idle warning prompt.
  StaffSessionPolicyEnabled = 'staff-session-policy-enabled',
  // BUG-873 — move up/down on a location's rooms in Core settings, which sets the Room View column
  // order. The sites gate fails OPEN, so create the flag in PostHog (active, 0% rollout) before
  // deploying. OFF => no reorder control and the reorder mutation is refused; an order already
  // saved still applies in Room View.
  RoomDisplayOrderEnabled = 'room-display-order-enabled',
  // OC-1297: gates the client-profile "Book visit" and "Check in to today's visit" actions
  // that book or check a client into several services at once. Fails CLOSED.
  MultiServiceVisitEnabled = 'multi-service-visit-enabled',
  // OC-1304: gates the form-builder custom property picker and writing mapped form answers to
  // client custom properties on submission. Fails CLOSED: submissions write only the document.
  FormCustomPropertyMappingEnabled = 'form-custom-property-mapping-enabled',
  // INT-1224: scored questionnaires (scoring config, templates, scored completion, results UI).
  // Fails CLOSED: scored writes return FEATURE_UNAVAILABLE and never fall back to unscored.
  ScoredQuestionnaires = 'scored-questionnaires-enabled',
  KioskDevicePairing = 'kiosk-device-pairing-enabled',
  // INT-1224: the KIOSK channel also needs KioskDevicePairing; MEMBER_APP needs MemberPendingForms.
  FormHandoff = 'form-handoff-enabled',
  MemberPendingForms = 'member-pending-forms-enabled',
  TanningCompliance = 'tanning-compliance-enabled',
  // INT-1224: recording a skin type assessed outside Hapana (staff mutation and the ClientSkinTypes
  // import). Fails CLOSED: the mutation and the import refuse with FEATURE_UNAVAILABLE.
  SkinTypeAssignment = 'skin-type-assignment-enabled',
  // INT-1224: a kiosk confirms first name and date of birth before a handed-off form opens. Fails
  // CLOSED: off keeps the first-name takeover and the device payload carries the first name.
  KioskIdentityConfirmation = 'kiosk-identity-confirmation-enabled',
  // INT-1224: a location may let an outstanding BOOKING form only remind instead of blocking the
  // booking in the app and embed. Fails CLOSED: off refuses writing REMIND and every member payload
  // says BLOCK.
  BookingGateMode = 'booking-gate-mode-enabled',
  // INT-1224: a UV bed's manufacturer exposure schedule kept as front-desk reference only; nothing
  // reads it to set or limit a timer. Fails CLOSED: off refuses the save and both reads with
  // FEATURE_UNAVAILABLE.
  EquipmentExposureSchedule = 'equipment-exposure-schedule-enabled',
  // MEM-989 — Plan your visit: the member-app widget (live occupancy and busyness forecast), the
  // gym information section on About us, and Location configuration rows 1.18–1.24. Read
  // fail-CLOSED on every side: OFF => the tab renders as before and the sites service refuses with
  // PLAN_YOUR_VISIT_DISABLED. The member app mirrors it by hand in mobile/src/constants.
  PlanYourVisitEnabled = 'plan-your-visit-enabled',
  // MEM-989 — location alerts, separate from PlanYourVisitEnabled so alerts roll out and back
  // independently: row 1.17, the Home banners and pop-up, and the site-alerts module. Read
  // fail-CLOSED; OFF => no alert surface renders and alert operations refuse with
  // LOCATION_ALERTS_DISABLED.
  LocationAlertsEnabled = 'location-alerts-enabled',
  // OC-1627 — unit being optional is a fix and is not behind this flag.
  AddressFormatsEnabled = 'address-formats-enabled',
  // OC-1933 — the one per-brand switch for equipment policy compliance and pending items on every
  // surface, enforcement included, so it is also the kill switch (D-07). Read fail-CLOSED; release
  // it on the corporate group and create it in PostHog (active, 0%) before this deploys.
  PolicyComplianceGate = 'policy-compliance-gate',
  // OC-2054 — switching location keeps the current page instead of opening the location's default
  // tab. Read fail-CLOSED: OFF => the switcher opens the default tab, as today. Create it in PostHog
  // (active, 0%) before this deploys.
  KeepPageOnLocationSwitch = 'keep-page-on-location-switch',
  // OC-2048 — tanning regulation overrides need one staff note per failing rule, and the doctor's
  // note range and "How these rules work" dialog show on the regulations pages. Read fail-CLOSED:
  // OFF => one override passes every overridable rule with no note, as today. Dev and staging
  // 100%, prod active at 0%; create it in PostHog before this deploys.
  TanningRegulationOverrides = 'tanning-regulation-overrides',
  // OC-2079 — Light / Dark / System appearance choice in Core. Read fail-CLOSED: OFF => Core stays
  // light and the choice is hidden, as today. Create it in PostHog (active, 0%) before this deploys.
  CoreDarkMode = 'core-dark-mode',
  // OC-2080 — "Download history" on a client's Schedule tab exports the member's bookings,
  // waitlist and pack-credit statement as one XLSX. Read fail-CLOSED: OFF => no button and the
  // export refuses with CLIENT_HISTORY_EXPORT_FEATURE_DISABLED. Create it in PostHog (active, 0%)
  // before this deploys, then release it to VAURA first.
  ClientHistoryExportEnabled = 'client-history-export-enabled',
}
